// ACTA_AGENT_CONTACT_AGENDA_ACTIONS_V3
// ACTA_AGENT_TWO_STAGE_PLANNER_V6
//
// Ce fichier ne réalise aucune écriture métier.
// Il prépare uniquement les données nécessaires pour ouvrir
// les workflows Acta existants.

import {
  prospects as prospectsApi,
} from "@/api";

import {
  normalizeAgentText,
} from "@/agent/entityResolver";

import {
  normalizeAgentMeetingQuery,
  resolveAgentMeeting,
} from "@/agent/resolverRegistry";


const MONTHS = {
  janvier: 0,
  fevrier: 1,
  mars: 2,
  avril: 3,
  mai: 4,
  juin: 5,
  juillet: 6,
  aout: 7,
  septembre: 8,
  octobre: 9,
  novembre: 10,
  decembre: 11,
};


const WEEKDAYS = {
  dimanche: 0,
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
};


const MEETING_SEARCH_STOPWORDS =
  new Set([
    "le",
    "la",
    "les",
    "un",
    "une",
    "de",
    "du",
    "des",
    "avec",
    "pour",
    "sur",
    "mon",
    "ma",
    "mes",
    "son",
    "sa",
    "ses",
    "rendez",
    "vous",
    "rdv",
    "reunion",
    "meeting",
    "modifie",
    "modifier",
    "change",
    "changer",
    "decale",
    "decaler",
    "deplace",
    "deplacer",
    "replanifie",
    "replanifier",
    "programme",
    "programmer",
    "planifie",
    "planifier",
    "cree",
    "creer",
    "resultat",
    "categorise",
    "categoriser",
    "honore",
    "realise",
    "effectue",
    "annule",
    "reporte",
    "absent",
    "absence",
    "client",
    "prospect",
    "contact",
  ]);


function localDateKey(
  date
) {
  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
}


function extractedClock(
  command
) {
  const match =
    String(command || "")
      .match(
        /\b(\d{1,2})(?:\s*h(?:\s*(\d{2}))?|:(\d{2}))\b/iu
      );

  if (!match) {
    return "";
  }

  const hours =
    Number(match[1]);

  const minutes =
    Number(
      match[2] ||
      match[3] ||
      0
    );

  if (
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return "";
  }

  return (
    `${String(hours).padStart(2, "0")}:`
    + `${String(minutes).padStart(2, "0")}`
  );
}


export function parseAgentDateTime(
  command,
  now = new Date()
) {
  const raw =
    String(
      command ||
      ""
    );

  const clean =
    normalizeAgentText(raw);

  const clock =
    extractedClock(raw);

  let selectedDate =
    null;

  if (
    /\baujourdhui\b/u.test(
      clean
    )
  ) {
    selectedDate =
      new Date(now);
  }

  if (
    !selectedDate &&
    /\bdemain\b/u.test(
      clean
    )
  ) {
    selectedDate =
      new Date(now);

    selectedDate.setDate(
      selectedDate.getDate() + 1
    );
  }

  if (!selectedDate) {
    const numeric =
      raw.match(
        /\b(\d{1,2})[/. -](\d{1,2})(?:[/. -](\d{2,4}))?\b/u
      );

    if (numeric) {
      const day =
        Number(numeric[1]);

      const month =
        Number(numeric[2]) - 1;

      let year =
        numeric[3]
          ? Number(
              numeric[3]
            )
          : now.getFullYear();

      if (
        year < 100
      ) {
        year += 2000;
      }

      const candidate =
        new Date(
          year,
          month,
          day
        );

      if (
        !numeric[3] &&
        candidate.getTime() <
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          ).getTime()
      ) {
        candidate.setFullYear(
          candidate.getFullYear()
          + 1
        );
      }

      if (
        !Number.isNaN(
          candidate.getTime()
        )
      ) {
        selectedDate =
          candidate;
      }
    }
  }

  if (!selectedDate) {
    const monthNames =
      Object.keys(MONTHS)
        .join("|");

    const textual =
      clean.match(
        new RegExp(
          `\\b(\\d{1,2})\\s+(${monthNames})(?:\\s+(\\d{4}))?\\b`,
          "u"
        )
      );

    if (textual) {
      const day =
        Number(textual[1]);

      const month =
        MONTHS[
          textual[2]
        ];

      const hasYear =
        Boolean(
          textual[3]
        );

      const year =
        hasYear
          ? Number(
              textual[3]
            )
          : now.getFullYear();

      const candidate =
        new Date(
          year,
          month,
          day
        );

      if (
        !hasYear &&
        candidate.getTime() <
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          ).getTime()
      ) {
        candidate.setFullYear(
          candidate.getFullYear()
          + 1
        );
      }

      selectedDate =
        candidate;
    }
  }

  if (!selectedDate) {
    for (
      const [
        name,
        weekday,
      ]
      of Object.entries(
        WEEKDAYS
      )
    ) {
      if (
        !new RegExp(
          `\\b${name}\\b`,
          "u"
        ).test(clean)
      ) {
        continue;
      }

      const candidate =
        new Date(now);

      candidate.setHours(
        0,
        0,
        0,
        0
      );

      let distance =
        (
          weekday -
          candidate.getDay() +
          7
        ) % 7;

      if (
        distance === 0
      ) {
        if (clock) {
          const [
            hour,
            minute,
          ] =
            clock
              .split(":")
              .map(Number);

          const requestedToday =
            new Date(now);

          requestedToday.setHours(
            hour,
            minute,
            0,
            0
          );

          distance =
            requestedToday.getTime() >
              now.getTime()
              ? 0
              : 7;
        } else {
          distance = 7;
        }
      }

      candidate.setDate(
        candidate.getDate()
        + distance
      );

      selectedDate =
        candidate;

      break;
    }
  }

  const date =
    selectedDate
      ? localDateKey(
          selectedDate
        )
      : "";

  return {
    date,
    start: clock,
    localDateTime:
      date && clock
        ? `${date}T${clock}`
        : "",
  };
}


function explicitDuration(
  command
) {
  const clean =
    normalizeAgentText(
      command
    );

  if (
    /\b(?:pendant|duree)\s+1\s+(?:h|heure)\s*30\b/u
      .test(clean)
  ) {
    return "90";
  }

  if (
    /\b(?:pendant|duree)\s+1\s+(?:h|heure)\b/u
      .test(clean)
  ) {
    return "60";
  }

  const match =
    clean.match(
      /\b(?:pendant|duree)\s+(\d{1,3})\s*(?:min|minute|minutes)\b/u
    );

  if (
    !match ||
    ![
      "15",
      "30",
      "45",
      "60",
      "90",
    ].includes(
      match[1]
    )
  ) {
    return "";
  }

  return match[1];
}


function explicitMeetingType(
  command
) {
  const clean =
    normalizeAgentText(
      command
    );

  if (
    /\b(?:physique|sur place|presentiel)\b/u
      .test(clean)
  ) {
    return "physical";
  }

  if (
    /\b(?:visio|visioconference|en ligne|teams|google meet|meet)\b/u
      .test(clean)
  ) {
    return "online";
  }

  return "";
}


function explicitMeetingSubject(
  command
) {
  const raw =
    String(
      command ||
      ""
    );

  const match =
    raw.match(
      /\b(?:objet|sujet)\s*:?\s*(.+)$/iu
    );

  return (
    match?.[1]
      ?.trim()
      ?.slice(
        0,
        300
      )
    || ""
  );
}


export function buildMeetingAgentPrefill(
  command
) {
  const dateTime =
    parseAgentDateTime(
      command
    );

  return {
    date:
      dateTime.date,
    start:
      dateTime.start,
    durationMinutes:
      explicitDuration(
        command
      ),
    meetingType:
      explicitMeetingType(
        command
      ),
    subject:
      explicitMeetingSubject(
        command
      ),
  };
}


export function buildMeetingAgentPrefillFromParams(
  params = {},
  fallbackCommand = ""
) {
  const dateCommand =
    [
      params?.dateExpression,
      params?.time,
    ]
      .filter(Boolean)
      .join(" ");

  const dateTime =
    parseAgentDateTime(
      dateCommand
      || fallbackCommand
    );

  const fallback =
    buildMeetingAgentPrefill(
      fallbackCommand
    );

  return {
    date:
      dateTime.date
      || fallback.date,
    start:
      String(
        params?.time
        || dateTime.start
        || fallback.start
        || ""
      ),
    durationMinutes:
      params?.durationMinutes
        !== null
      && params?.durationMinutes
        !== undefined
        ? String(
            params.durationMinutes
          )
        : fallback
          .durationMinutes,
    meetingType:
      String(
        params?.meetingType
        || fallback
          .meetingType
        || ""
      ),
    subject:
      String(
        params?.subject
        || fallback.subject
        || ""
      ),
  };
}


export function extractAgentNoteContent(
  command
) {
  const raw =
    String(
      command ||
      ""
    ).trim();

  const colon =
    raw.indexOf(":");

  if (
    colon >= 0 &&
    colon <
      raw.length - 1
  ) {
    return raw
      .slice(
        colon + 1
      )
      .trim()
      .slice(
        0,
        2000
      );
  }

  return raw.slice(
    0,
    2000
  );
}


export function extractAgentFollowupLabel(
  command
) {
  return String(
    command ||
    ""
  )
    .trim()
    .slice(
      0,
      500
    );
}


export function meetingOutcomeFromCommand(
  command
) {
  const clean =
    normalizeAgentText(
      command
    );

  if (
    /\b(?:non honore|absent|absence|ne sest pas presente)\b/u
      .test(clean)
  ) {
    return "no_show";
  }

  if (
    /\bannule\s+par\s+(?:le\s+)?(?:client|prospect)\b/u
      .test(clean)
  ) {
    return "cancelled_by_prospect";
  }

  if (
    /\bannule\s+par\s+(?:nous|notre equipe)\b/u
      .test(clean)
  ) {
    return "cancelled_by_us";
  }

  if (
    /\b(?:replanifie|reprogramme|reporte)\b/u
      .test(clean)
  ) {
    return "rescheduled";
  }

  if (
    /\b(?:a recontacter|recontacter|a relancer)\b/u
      .test(clean)
  ) {
    return "contact_again";
  }

  if (
    /\b(?:honore|realise|effectue|a eu lieu)\b/u
      .test(clean)
  ) {
    return "honored";
  }

  return "";
}


export function meetingAgentKey(
  meeting
) {
  return (
    meeting?.nextActionNoteId
    || meeting?.meetingId
    || meeting?.id
    || ""
  );
}


export function meetingDisplayLabel(
  meeting
) {
  const contact =
    [
      meeting?.firstName,
      meeting?.lastName,
    ]
      .filter(Boolean)
      .join(" ")
    || meeting?.company
    || "Contact";

  const subject =
    meeting?.nextAction
    || "Rendez-vous";

  return `${contact} — ${subject}`;
}


function meetingSearchCommand(
  command
) {
  return normalizeAgentText(
    command
  )
    .split(" ")
    .filter(
      (token) =>
        token.length >= 2 &&
        !MEETING_SEARCH_STOPWORDS
          .has(token)
    )
    .join(" ");
}


export async function prepareMeetingAction({
  command = "",
  meetingQuery = "",
  contactQuery = "",
}) {
  const response =
    await prospectsApi.agenda();

  const agenda =
    Array.isArray(response)
      ? response
      : [];

  const meetings =
    agenda.filter(
      (item) =>
        item?.nextActionType ===
        "meeting"
    );

  const semanticMeetingQuery =
    normalizeAgentMeetingQuery({
      meetingQuery,
      contactQuery,
    });

  const fallbackQuery =
    !semanticMeetingQuery
      ? meetingSearchCommand(
          command
        )
      : "";

  const resolution =
    resolveAgentMeeting({
      items:
        meetings,
      meetingQuery:
        semanticMeetingQuery
        || fallbackQuery,
      contactQuery,
    });

  return {
    meetingMatches:
      resolution.matches
      || [],
    meetingResolution:
      resolution,
    meetingQuery:
      resolution.query
      || "",
  };
}
